/**
 * アクセスログ（監査証跡）の閲覧。
 *
 * 2つのログを切り替えて見る:
 * - ダウンロード履歴（download_logs）: **拒否された要求も記録される**。
 *   「誰が何を落としたか」だけでなく「誰が何を落とそうとして断られたか」も
 *   見えることに意味がある（権利設定の間違いはここに現れる）。
 * - 操作ログ（audit_logs）: ユーザー・招待・ライセンス・設定の変更履歴。
 *   assets は対象外（変換の自動状態遷移でノイズになるため）。
 *
 * どちらも追記専用。素材が削除済みでも読める。記録時に素材名を
 * 焼き付けてあるため（詳細は 20260819010000 のマイグレーション）。
 */
import { useEffect, useState } from 'react'
import type { Profile } from '../lib/supabase.ts'
import { can } from '../lib/roles.ts'
import { AUDIT_ACTION_LABEL } from '../lib/format.ts'
import {
  getAccessLogs,
  listAuditLogs,
  ApiError,
  type AccessLog,
  type AuditLog,
} from '../lib/api.ts'

const KIND_LABEL: Record<string, string> = {
  master: '元本',
  proxy: 'プロキシ',
  thumbnail: 'サムネイル',
  zip: '一括',
}

export function AccessLogs({ profile }: { profile: Profile }) {
  // このページ自体が logs.read でゲートされているが、出し分けもマトリクスに揃える
  const showAudit = can(profile.role, 'logs.read')
  const [mode, setMode] = useState<'downloads' | 'audit'>('downloads')

  return (
    <div className="pad logs">
      <h2>アクセスログ</h2>

      <div className="log-filters">
        <button
          className={mode === 'downloads' ? 'chip active' : 'chip'}
          onClick={() => setMode('downloads')}
        >
          ダウンロード履歴
        </button>
        {showAudit && (
          <button
            className={mode === 'audit' ? 'chip active' : 'chip'}
            onClick={() => setMode('audit')}
          >
            操作ログ
          </button>
        )}
      </div>

      {mode === 'downloads' ? <DownloadLogs /> : <AuditLogs />}
    </div>
  )
}

function DownloadLogs() {
  const [logs, setLogs] = useState<AccessLog[]>([])
  const [outcome, setOutcome] = useState<'all' | 'granted' | 'denied'>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getAccessLogs({ limit: 200, outcome: outcome === 'all' ? undefined : outcome })
      .then((res) => {
        if (!cancelled) setLogs(res.logs)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.code : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [outcome])

  return (
    <>
      <p className="muted small">
        ダウンロードの監査証跡です。追記のみで、後から書き換えられません。
        拒否された要求も記録されるので、権利設定の誤りはここに現れます。
      </p>

      <div className="log-filters">
        {(['all', 'granted', 'denied'] as const).map((o) => (
          <button
            key={o}
            className={outcome === o ? 'chip active' : 'chip'}
            onClick={() => setOutcome(o)}
          >
            {o === 'all' ? 'すべて' : o === 'granted' ? '許可' : '拒否'}
          </button>
        ))}
      </div>

      {loading && <p className="muted small">読み込み中…</p>}
      {error && <p className="error small">読み込めませんでした（{error}）</p>}

      {!loading && !error && logs.length === 0 && (
        <p className="muted small">記録がありません。</p>
      )}

      {logs.length > 0 && (
        <div className="log-scroll">
          <table className="logtable">
            <thead>
              <tr>
                <th>日時</th>
                <th>利用者</th>
                <th>組織</th>
                <th>素材</th>
                <th>種別</th>
                <th>結果</th>
                <th>接続元</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className={log.outcome === 'denied' ? 'denied' : undefined}>
                  <td className="small nowrap">
                    {new Date(log.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td className="small">{log.users?.display_name ?? log.users?.email ?? '—'}</td>
                  <td className="small">{log.organizations?.name ?? '—'}</td>
                  <td className="small">
                    {log.asset_title ?? <span className="muted">（削除済み）</span>}
                    {log.asset_file_name && <span className="muted"> / {log.asset_file_name}</span>}
                  </td>
                  <td className="small">{KIND_LABEL[log.kind] ?? log.kind}</td>
                  <td className="small">
                    {log.outcome === 'granted' ? (
                      <span className="success">許可</span>
                    ) : (
                      <span className="error">拒否（{log.denial_reason}）</span>
                    )}
                  </td>
                  <td className="mono small nowrap">{log.ip ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/**
 * 操作ログ（audit_logs）。
 *
 * action の日本語ラベルは format.ts の AUDIT_ACTION_LABEL に集約してある。
 * settings 系など DB 実装に依存する action は未知でも生値で表示される。
 */
function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listAuditLogs({ limit: 200 })
      .then((res) => {
        if (!cancelled) setLogs(res.logs)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.code : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <>
      <p className="muted small">
        ユーザー・招待・ライセンス・設定の変更履歴です。追記のみで、後から書き換えられません。
        素材の変更は対象外です（変換の自動状態遷移でノイズになるため）。
      </p>

      {loading && <p className="muted small">読み込み中…</p>}
      {error && <p className="error small">読み込めませんでした（{error}）</p>}

      {!loading && !error && logs.length === 0 && (
        <p className="muted small">記録がありません。</p>
      )}

      {logs.length > 0 && (
        <div className="log-scroll">
          <table className="logtable">
            <thead>
              <tr>
                <th>日時</th>
                <th>操作者</th>
                <th>操作</th>
                <th>対象</th>
                <th>詳細</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="small nowrap">
                    {new Date(log.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td className="small">{log.users?.display_name ?? log.users?.email ?? '—'}</td>
                  <td className="small">{AUDIT_ACTION_LABEL[log.action] ?? log.action}</td>
                  <td className="mono small">
                    {log.target_table}
                    {log.target_id && <span className="muted"> / {log.target_id}</span>}
                  </td>
                  <td className="small">
                    {log.detail ? (
                      <details>
                        <summary className="muted small">表示</summary>
                        <pre className="mono small audit-detail">
                          {JSON.stringify(log.detail, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
